import type { PaneState } from "../types.js";
import { formatPct } from "./format.js";

export type BarColor = "call" | "put";
export type BarFill = "solid" | "hollow"; // solid = at ask, hollow = at bid

export interface BarDescriptor {
  key: "putsBid" | "putsAsk" | "callsBid" | "callsAsk";
  label: string; // "at bid" | "at ask"
  group: BarColor;
  fill: BarFill;
  pct: number; // current %
  highWaterPct: number; // intraday peak %
  belowHigh: boolean; // true when the bar sits meaningfully below its high
  hiLabel: string; // "hi NN" (meaningful when belowHigh)
}

// Don't flag "below high" for sub-half-point rounding noise.
const HI_EPSILON = 0.5;

function makeBar(
  key: BarDescriptor["key"],
  group: BarColor,
  fill: BarFill,
  pct: number,
  highWaterPct: number,
): BarDescriptor {
  return {
    key,
    label: fill === "solid" ? "at ask" : "at bid",
    group,
    fill,
    pct,
    highWaterPct,
    belowHigh: highWaterPct - pct > HI_EPSILON,
    hiLabel: `hi ${Math.round(highWaterPct)}`,
  };
}

/** Four bars in put-before-call order: puts bid, puts ask, calls bid, calls ask. */
export function toBars(pane: PaneState): BarDescriptor[] {
  const { composition, highWater } = pane;
  return [
    makeBar("putsBid", "put", "hollow", composition.puts.bidPct, highWater.putsBidPct),
    makeBar("putsAsk", "put", "solid", composition.puts.askPct, highWater.putsAskPct),
    makeBar("callsBid", "call", "hollow", composition.calls.bidPct, highWater.callsBidPct),
    makeBar("callsAsk", "call", "solid", composition.calls.askPct, highWater.callsAskPct),
  ];
}

export interface BetweenReadout {
  group: BarColor;
  label: string;
}

/** Per-side between-spread text readouts (puts first, to match bar order). */
export function betweenReadouts(pane: PaneState): BetweenReadout[] {
  return [
    { group: "put", label: `puts · between spread ${formatPct(pane.composition.puts.betweenPct)}` },
    { group: "call", label: `calls · between spread ${formatPct(pane.composition.calls.betweenPct)}` },
  ];
}
